import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, DollarSign, BarChart2, Users } from 'lucide-react';

const Investors = () => {
  return (
    <div className="">
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm font-medium mb-6">Investor Relations</div>
            <h1 className="font-heading text-5xl font-bold leading-tight mb-6">Invest in India's <span className="text-secondary-400">Healthcare Future</span></h1>
            <p className="text-white/80 text-lg leading-relaxed mb-8">APLUS Healthcare Solutions is positioned to capture a significant share of India's rapidly growing pharmacy retail market. We invite forward-thinking investors to partner with us in this transformative journey.</p>
            <Link to="/contact" className="btn-secondary">Connect with Our Team <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '₹1,700 Cr+', label: 'India Pharma Retail Market' },
              { value: '15% CAGR', label: 'Market Growth Rate' },
              { value: '100+', label: 'Target Outlets by 2029' },
              { value: '₹25 Cr+', label: 'Revenue Target FY 2026-27' },
            ].map((s, i) => (
              <div key={i}>
                <div className="font-heading text-3xl font-bold text-primary-600 mb-1">{s.value}</div>
                <div className="text-gray-600 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Why Invest in <span className="gradient-text">APlus Healthcare</span>?</h2>
            <p className="section-subtitle mx-auto">A compelling investment opportunity in one of India's most essential and growing sectors.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { icon: TrendingUp, title: 'Massive Market Opportunity', desc: "India's pharma retail market is valued at over ₹1,700 crore and growing at 15% CAGR. With low pharmacy density compared to global standards, there is enormous room for organized retail chains like APlus Pharmacy to grow.", color: 'text-primary-600', bg: 'bg-primary-50' },
              { icon: BarChart2, title: 'Scalable Business Model', desc: 'Our asset-light franchise model enables rapid expansion with low capital deployment. Each new franchise outlet is profitable within 18-24 months, creating a self-sustaining growth engine.', color: 'text-secondary-600', bg: 'bg-secondary-50' },
              { icon: DollarSign, title: 'Strong Unit Economics', desc: 'APlus Pharmacy outlets generate gross margins of 25-35% with lean operating costs. Our focus on generic medicines (which carry higher margins) further improves profitability at the outlet level.', color: 'text-amber-600', bg: 'bg-amber-50' },
              { icon: Users, title: 'Experienced Leadership', desc: 'Our founding team brings deep expertise in pharmacy retail, supply chain management, technology, and business operations — ensuring disciplined execution of our growth strategy.', color: 'text-rose-600', bg: 'bg-rose-50' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className={`w-14 h-14 ${item.bg} rounded-2xl flex items-center justify-center mb-5`}>
                  <item.icon className={`w-7 h-7 ${item.color}`} />
                </div>
                <h3 className="font-heading font-bold text-gray-900 text-xl mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="section-title mb-6">Our <span className="gradient-text">Growth Roadmap</span></h2>
              <div className="space-y-6">
                {[
                  { phase: 'Phase 1 (2024-2025)', title: 'Foundation & First Outlet — Salem', targets: ['Launch first APlus Pharmacy outlet in Salem, Tamil Nadu', 'Build supply chain with licensed distributors', 'Establish brand identity, digital presence & SOPs', 'Validate franchise model and unit economics'] },
                  { phase: 'Phase 2 (2025-2027)', title: 'Tamil Nadu Expansion', targets: ['10–25 outlets across Tamil Nadu', 'Expand to key cities: Chennai, Coimbatore, Madurai', '₹2–5 Cr annual revenue target', 'Seed / Angel round fundraising'] },
                  { phase: 'Phase 3 (2027-2030)', title: 'South India & Pan-India Scale', targets: ['100+ outlets across South India', 'Expand to Karnataka, Andhra Pradesh, Kerala', 'Proprietary private label products', '₹25 Cr+ revenue target & Series A'] },
                ].map((phase, i) => (
                  <div key={i} className="border border-gray-100 rounded-2xl p-6">
                    <div className="inline-flex items-center bg-primary-100 text-primary-700 rounded-full px-3 py-1 text-xs font-semibold mb-3">{phase.phase}</div>
                    <h3 className="font-heading font-bold text-gray-900 mb-3">{phase.title}</h3>
                    <ul className="space-y-2">
                      {phase.targets.map((t, j) => (
                        <li key={j} className="flex items-center gap-2 text-gray-600 text-sm">
                          <div className="w-1.5 h-1.5 bg-secondary-500 rounded-full flex-shrink-0"></div>
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-3xl p-8 text-white">
              <h3 className="font-heading text-2xl font-bold mb-6">Investment Highlights</h3>
              <div className="space-y-5">
                {[
                  { label: 'Company Stage', value: 'Early Growth (Seed / Pre-Series A)' },
                  { label: 'Business Type', value: 'Pharmacy Retail Chain (B2C + Franchise)' },
                  { label: 'Current Presence', value: '1 Outlet, Salem, Tamil Nadu' },
                  { label: 'Revenue Model', value: 'Retail Sales + Franchise Fees + Royalty' },
                  { label: 'Target Raise', value: 'Confidential — Available on Request' },
                  { label: 'Use of Funds', value: 'Expansion, Technology, Working Capital' },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-start border-b border-white/20 pb-4">
                    <span className="text-white/70 text-sm">{item.label}</span>
                    <span className="text-white font-semibold text-sm text-right max-w-xs">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/contact" className="bg-white text-primary-700 hover:bg-gray-100 font-semibold py-3 px-6 rounded-xl transition-all duration-300 inline-flex items-center gap-2 w-full justify-center">
                  Request Investor Deck <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl font-bold text-white mb-4">Ready to Explore Investment?</h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">Contact our investor relations team to receive our detailed investor deck, financials, and business plan.</p>
          <Link to="/contact" className="bg-white text-primary-700 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg inline-flex items-center gap-2">
            Contact Investor Relations <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Investors;
